/**
 * Landing.jsx — Phase 19.1b-i (CODA v5 Science Lab spec).
 *
 * Complete rebuild top-to-bottom. Ten sections, dark hero + closing
 * CTA + footer as the only three dark surfaces on the page (per v5
 * "one dark section per page except hero/footer" rule).
 *
 * Motion inventory (all skipped under `prefers-reduced-motion`):
 *   • Hero GSAP timeline — eyebrow → words (stagger) → sub → ctas → trust
 *     → floating cards, then a repeating sine oscillation on the cards.
 *   • Accuracy stats — GSAP scroll-triggered counter animation
 *     (value counts from 0 → target when the stat scrolls into view).
 *   • Global `.reveal` fade-up-on-scroll (wired by App.js → attachReveals()).
 *   • Nav border-bottom ramp on scroll-past-80px (owned by Nav.jsx).
 *
 * Live-data wiring per spec: `/api/public/stats` returns
 *   { analyses_sealed, domains, calibration: { direction_accuracy_pct } }.
 * We wire two of the four stats to real numbers with aspirational
 * fallbacks; the other two stay as the spec's placeholder values.
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import {
    ArrowRight, ChevronRight, Sparkles, Fingerprint, GitBranch,
    Network, LayoutGrid, Activity, TrendingUp,
} from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ThreeLayerArchitectureSection from "../components/landing/ThreeLayerArchitectureSection";
import SeeItInActionSection from "../components/landing/SeeItInActionSection";
import LandingImpactCard from "../components/landing/LandingImpactCard";
import HeroBackground from "../components/landing/HeroBackground";
import CascadePreview from "../components/marketing/CascadePreview";
import BacktestSparkline from "../components/marketing/BacktestSparkline";
import { useLandingTelemetry, useImpressionObserver, useHeroEngagementBeacon } from "../hooks/useLandingTelemetry";
import { gsap, ScrollTrigger } from "../lib/motion";
import { useBacktestSummary } from "../hooks/useBacktestSummary";
import { HERO_MATH_STRINGS } from "../components/landing/heroMathStrings";
import "./Landing.css";

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────────────────────────────────────────────────────
 * Live-stats hook — returns null until /api/public/stats resolves.
 * ────────────────────────────────────────────────────────────────── */
function useLiveStats() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`${API}/api/public/stats`, { cache: "no-store" });
                if (!r.ok) return;
                const d = await r.json();
                if (!cancelled) setStats(d);
            } catch (_) { /* stats degrade silently to fallback placeholders */ }
        })();
        return () => { cancelled = true; };
    }, []);
    return stats;
}

const HERO_H1 = "Every major decision has a consequence cascade.";
const HERO_SUB =
    "Tomauro maps the full multi-domain consequence of any institutional decision, with a cryptographic audit record that proves it.";

/* ─────────────────────────────────────────────────────────────────────
 * HERO
 * ────────────────────────────────────────────────────────────────── */
function HeroSection() {
    const reduced = useReducedMotion();
    // Phase Y post-release patch — wire the hero primary CTA to fire
    // `landing_hero_cta_click`. Kept out of a component prop so we
    // can reuse the shared `useLandingTelemetry` session.
    const { fire } = useLandingTelemetry();
    // Phase v5.3 §9.4 — attach the >2s cursor-dwell engagement beacon
    // to the hero section root. Fires `landing_hero_engagement` once
    // per session; DNT/touch/reduced-motion are handled internally.
    const heroRef = useRef(null);
    // Phase HERO-FINAL — static math typography canvas (z-index 0,
    // behind the grid texture, one draw on mount + on resize).
    const mathCanvasRef = useRef(null);
    useHeroEngagementBeacon(heroRef, { dwellMs: 2000 });

    // Phase HERO-FINAL — one static draw of the math typography onto
    // the canvas, redraw on resize. Explicitly NO requestAnimationFrame
    // loop — the effect is decorative, not animated.
    useEffect(() => {
        const canvas = mathCanvasRef.current;
        const hero = heroRef.current;
        if (!canvas || !hero) return;

        const draw = () => {
            const W = hero.offsetWidth;
            const H = hero.offsetHeight;
            if (!W || !H) return;
            const dpr = window.devicePixelRatio || 1;
            canvas.width  = W * dpr;
            canvas.height = H * dpr;
            canvas.style.width  = `${W}px`;
            canvas.style.height = `${H}px`;
            const ctx = canvas.getContext("2d");
            if (!ctx) return;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, W, H);

            // Pass 1 — main math strings, 5–12% opacity, 9–12px.
            HERO_MATH_STRINGS.forEach((str, i) => {
                const r1 = (i * 137.508 + 23) % 1;
                const r2 = (i * 98.621  + 61) % 1;
                const r3 = (i * 54.321  + 11) % 1;
                ctx.save();
                ctx.globalAlpha = 0.05 + r3 * 0.065;
                ctx.fillStyle = "#FFFFFF";
                ctx.font = `400 ${9 + Math.floor(r3 * 4)}px 'JetBrains Mono', 'Courier New', monospace`;
                ctx.fillText(str, (r1 * 0.86 + 0.07) * W, (r2 * 0.80 + 0.11) * H);
                ctx.restore();
            });

            // Pass 2 — subset overlay, 2–5% opacity, 8–10px, different jitter.
            HERO_MATH_STRINGS.slice(0, 24).forEach((str, i) => {
                const r1 = (i * 231.7 + 77) % 1;
                const r2 = (i * 163.4 + 44) % 1;
                const r3 = (i * 87.3  + 19) % 1;
                ctx.save();
                ctx.globalAlpha = 0.02 + r3 * 0.03;
                ctx.fillStyle = "#FFFFFF";
                ctx.font = `400 ${8 + Math.floor(r3 * 3)}px 'JetBrains Mono', 'Courier New', monospace`;
                ctx.fillText(str, (r1 * 0.84 + 0.08) * W, (r2 * 0.78 + 0.14) * H);
                ctx.restore();
            });
        };
        draw();

        let raf = null;
        const onResize = () => {
            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(draw);
        };
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    useEffect(() => {
        if (reduced) return;
        // Ensure elements exist before the timeline starts.
        const ctx = gsap.context(() => {
            // 2026-07-29 CHANGE 47 (P0 hero-vanish fix) — `immediateRender:
            // false` on the timeline defaults so `.from(el, { opacity: 0 })`
            // does NOT immediately set inline opacity: 0 on the DOM. If the
            // tween ever gets orphaned mid-play (StrictMode double-mount,
            // framer-motion route-wrapper race, fast browser reload
            // interrupting RAF), elements stay at their CSS default
            // (opacity: 1) rather than being locked invisible. The tween
            // still animates fade-up-in when it plays; the difference is
            // "silent failure = still visible" instead of "silent failure
            // = blank page".
            const tl = gsap.timeline({ defaults: { ease: "power3.out", immediateRender: false } });
            tl.from(".hero-eyebrow", { opacity: 0, y: 20, duration: 0.6 })
              .from(".hero-word", { opacity: 0, y: 32, duration: 0.9, stagger: 0.04 }, "-=0.2")
              .from(".hero-sub", { opacity: 0, y: 20, duration: 0.6 }, "-=0.5")
              .from(".hero-ctas", { opacity: 0, y: 20, duration: 0.6 }, "-=0.3")
              .from(".hero-trust", { opacity: 0, y: 20, duration: 0.6 }, "-=0.3")
              .from(".hero-floating-card", { opacity: 0, scale: 0.9, duration: 0.8, stagger: 0.15 }, "-=0.5");

            // Phase HERO-FINAL — highlight rectangles on "decision" and
            // "consequence". Absolute delays keep the reveal aligned
            // with the parent hero-word stagger regardless of how the
            // wrapping words appear in the headline.
            gsap.from(".hl-word", {
                opacity: 0, y: 3,
                duration: 0.45,
                ease: "power2.out",
                stagger: 0.17,
                delay: 0.65,
                immediateRender: false,
            });

            // Oscillating floating cards — sine loop, desynced per card.
            gsap.utils.toArray(".hero-floating-card").forEach((card, i) => {
                gsap.to(card, {
                    y: "+=10",
                    duration: 4 + i * 0.4,
                    ease: "sine.inOut",
                    yoyo: true,
                    repeat: -1,
                    delay: i * 0.5,
                });
            });

            // Phase I P2 — scroll-linked parallax layers. Reads as depth
            // without moving anything off-canvas. Each layer has its own
            // scrub speed so the hero feels three-dimensional even under
            // Lenis smooth scroll. Reduced-motion is already handled by
            // the early-return above.
            gsap.to(".hero-grid", {
                yPercent: 22,
                ease: "none",
                scrollTrigger: {
                    trigger: ".hero-root",
                    start: "top top",
                    end: "bottom top",
                    scrub: true,
                },
            });
            gsap.to(".hero-floating-card--a", {
                yPercent: -30, xPercent: -6, rotate: -6,
                ease: "none",
                scrollTrigger: { trigger: ".hero-root", start: "top top", end: "bottom top", scrub: true },
            });
            gsap.to(".hero-floating-card--b", {
                yPercent: -60, xPercent: 5, rotate: 6,
                ease: "none",
                scrollTrigger: { trigger: ".hero-root", start: "top top", end: "bottom top", scrub: true },
            });
            gsap.to(".hero-floating-card--c", {
                yPercent: -80, xPercent: 8, rotate: -4,
                ease: "none",
                scrollTrigger: { trigger: ".hero-root", start: "top top", end: "bottom top", scrub: true },
            });
            gsap.to(".hero-words", {
                yPercent: 18, opacity: 0.75,
                ease: "none",
                scrollTrigger: { trigger: ".hero-root", start: "top top", end: "bottom top", scrub: true },
            });
        });
        return () => ctx.revert();
    }, [reduced]);

    return (
        <section className="hero-root" ref={heroRef} data-testid="hero-section">
            {/* Phase HERO-FINAL — static math typography background.
                z-index 0 sits behind the grid texture (::before at z-1).
                pointer-events: none so it never intercepts clicks. */}
            <canvas
                ref={mathCanvasRef}
                className="hero-math-canvas"
                aria-hidden="true"
            />
            <div className="hero-grid" aria-hidden="true" />
            {/* Phase v5.3 §9.3 — cursor-reactive grid + canvas pulses +
                ambient math-notation values. Absolute-positioned inside
                the hero, sits behind the content, capped z-index so the
                floating cards + copy still layer on top. */}
            <HeroBackground />

            {/* Floating data cards — plausible fake data (Phase 19.2 wires real) */}
            <FloatingDataCard
                className="hero-floating-card--a"
                dotColor="#34D399"
                label="Employment"
                title="Moderate Positive"
                fill={62}
                marker={65}
            />
            <FloatingDataCard
                className="hero-floating-card--b"
                dotColor="#FB7185"
                label="Consumer Prices"
                title="Mild Negative"
                fill={38}
                marker={40}
            />
            <FloatingDataCard
                className="hero-floating-card--c"
                dotColor="#60A5FA"
                label="Political Capital"
                title="Strong Positive"
                fill={78}
                marker={80}
            />

            <div className="hero-inner max-w-wide mx-auto px-6 sm:px-10 pt-40 pb-32 flex flex-col items-center text-center">
                <div className="hero-eyebrow inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-8">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-light" />
                    <span className="text-[11px] uppercase tracking-widest font-semibold text-white/70">
                        Institutional Decision Intelligence
                    </span>
                </div>

                <h1
                    className="hero-words font-heading text-white font-bold max-w-[820px]"
                    style={{
                        fontSize: "clamp(2.75rem, 6.2vw, 4.5rem)",
                        lineHeight: "1.04",
                        letterSpacing: "-0.025em",
                    }}
                >
                    {HERO_H1.split(" ").map((w, i) => {
                        // Phase HERO-FINAL — swap the class for "decision"
                        // and "consequence" so the highlight rectangle +
                        // corner-dot decoration renders on those two
                        // words. Trailing punctuation is stripped only
                        // for the match, not the display text.
                        const cleaned = w.replace(/[.,;:!?]$/g, "").toLowerCase();
                        const isHl = cleaned === "decision" || cleaned === "consequence";
                        return (
                            <span
                                key={i}
                                className={isHl ? "hero-word hl-word" : "hero-word"}
                            >
                                {w}
                            </span>
                        );
                    })}
                </h1>

                <p className="hero-sub text-[17px] leading-relaxed text-white/70 max-w-[560px] mt-7">
                    {HERO_SUB}
                </p>

                <div className="hero-ctas flex flex-col sm:flex-row items-center gap-3 mt-10">
                    <Link
                        to="/analyses/new"
                        data-testid="hero-cta-primary"
                        onClick={() => fire("landing_hero_cta_click")}
                        className="group inline-flex items-center gap-2 h-12 px-6 bg-brand text-white rounded-md font-medium text-[15px]
                                   shadow-lg hover:bg-brand-dark hover:-translate-y-0.5 hover:shadow-xl
                                   transition-all duration-200 ease-out"
                    >
                        Run an Analysis
                        <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                    {/* The demo CTA is hidden (#381). The analyses it pointed
                        at do not exist in this database, so
                        /public/demo-links returned an empty list and the
                        button quietly sent visitors to /register instead --
                        a control promising a demo and delivering a signup
                        form. Restore it once the demo analyses are selected
                        from the data rather than from hard-coded ids; #381
                        has the options. */}
                    <Link
                        to="/how-it-works"
                        data-testid="hero-cta-secondary"
                        className="group inline-flex items-center gap-2 h-12 px-6 rounded-md text-white/90 text-[15px] font-medium
                                   border border-white/25 hover:bg-white/5 hover:border-white/45
                                   transition-all duration-200 ease-out"
                    >
                        See how it works
                        <ArrowRight size={16} className="opacity-70 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                </div>

                <div className="hero-trust mt-16 flex flex-col items-center gap-4">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-white/75">
                        Trusted framework · Cryptographic audit · Deterministic replay
                    </div>
                    <div className="flex items-center gap-3 opacity-90">
                        <span className="hero-trust-logo">Institutional</span>
                        <span className="hero-trust-logo">Sovereign</span>
                        <span className="hero-trust-logo">Regulator</span>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FloatingDataCard({ className, dotColor, label, title, fill, marker }) {
    return (
        <div className={`hero-floating-card ${className}`} aria-hidden="true">
            <div className="hero-floating-card__label">
                <span className="hero-floating-card__dot" style={{ background: dotColor }} />
                {label}
            </div>
            <div className="hero-floating-card__title">{title}</div>
            <div className="hero-floating-card__bar">
                <div className="hero-floating-card__bar-fill" style={{ width: `${fill}%` }} />
                <div className="hero-floating-card__bar-marker" style={{ left: `calc(${marker}% - 1px)` }} />
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────────
 * HOW IT WORKS — 3-col INPUT → ENGINE → OUTPUT
 * ────────────────────────────────────────────────────────────────── */
// 2026-07-27 Round 4 · CHANGE 21 — redesigned How-it-works.
//
// Three steps, horizontal on desktop / vertical on mobile. Each step
// carries a small styled visual mockup built from native design
// tokens: a wizard-input stub, a mini cascade grid (mirrors Round 3
// CHANGE 16), and three perspective-tilted preview cards.
//
// Position: LAST content section on the page — rendered above
// `<Footer />`. Zero numbers, zero accuracy claims, zero methodology
// links. Respects `prefers-reduced-motion` by falling back to a
// static reveal.
function HowItWorksSection() {
    const steps = [
        {
            n: 1, tid: "howitworks-step-1",
            title: "Pose the decision",
            copy:  "State the institutional decision you're weighing. Tomauro handles the framing.",
            visual: <StepOneVisual />,
        },
        {
            n: 2, tid: "howitworks-step-2",
            title: "Tomauro maps the consequences",
            copy:  "The engine traces how the decision propagates across interconnected domains and time horizons.",
            visual: <StepTwoVisual />,
        },
        {
            n: 3, tid: "howitworks-step-3",
            title: "Read, brief, act",
            copy:  "Get a decision brief you can share with your board, a chain of custody you can prove, and a set of concrete next steps.",
            visual: <StepThreeVisual />,
        },
    ];
    return (
        <section className="bg-surface py-24 sm:py-28" data-testid="howitworks-section">
            <div className="max-w-wide mx-auto px-6 sm:px-10">
                <div className="mb-14 max-w-2xl reveal">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-brand mb-3">
                        How Tomauro works
                    </div>
                    <h2 className="font-heading text-[32px] sm:text-[40px] font-semibold text-ink-1 tracking-tight leading-[1.1]">
                        Three steps from decision to defensible.
                    </h2>
                </div>

                {/* Desktop: horizontal 3-col. Mobile: vertical stack with a
                    subtle indigo progress rail down the left edge. */}
                <div className="hidden md:grid md:grid-cols-3 md:gap-8">
                    {steps.map((s, i) => (
                        <div
                            key={s.n}
                            data-testid={s.tid}
                            className="reveal"
                            style={{ transitionDelay: `${i * 100}ms` }}
                        >
                            <div className="mb-6 h-64 flex items-end">
                                {s.visual}
                            </div>
                            <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-ink-3 mb-2">
                                Step {String(s.n).padStart(2, "0")}
                            </div>
                            <h3 className="font-heading text-[22px] font-semibold text-ink-1 tracking-tight mb-3">
                                {s.title}
                            </h3>
                            <p className="text-[15px] leading-[1.75] text-ink-2 max-w-sm font-serif">
                                {s.copy}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Mobile vertical layout with a progress rail. */}
                <ol className="md:hidden space-y-10 relative pl-8 border-l border-brand/30">
                    {steps.map((s) => (
                        <li key={s.n} data-testid={s.tid} className="relative reveal">
                            <span
                                className="absolute -left-[36px] top-0 w-6 h-6 rounded-full border-2 border-brand bg-surface text-brand flex items-center justify-center text-[11px] font-mono font-semibold"
                                aria-hidden
                            >
                                {s.n}
                            </span>
                            <div className="mb-5 max-h-56">
                                {s.visual}
                            </div>
                            <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-ink-3 mb-2">
                                Step {String(s.n).padStart(2, "0")}
                            </div>
                            <h3 className="font-heading text-[20px] font-semibold text-ink-1 tracking-tight mb-2">
                                {s.title}
                            </h3>
                            <p className="text-[14.5px] leading-[1.7] text-ink-2 font-serif">
                                {s.copy}
                            </p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
}

// ── Step 1 — styled wizard-input mockup with a blinking cursor ────
function StepOneVisual() {
    return (
        <div className="w-full rounded-lg border border-border bg-panel/60 shadow-sm p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-3 mb-3">
                Decision
            </div>
            <div className="font-heading text-[15px] text-ink-1 leading-snug">
                Should we invest in renewable energy for our data centers
                <span
                    className="inline-block w-[2px] h-4 ml-1 -mb-0.5 bg-brand"
                    style={{ animation: "hiw-cursor 1.1s steps(1) infinite" }}
                    aria-hidden
                />
            </div>
            <div className="mt-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-ink-3">
                <span>Actor · Board</span>
                <span>Horizon · 5 yr</span>
            </div>
            <style>{`
                @keyframes hiw-cursor { 50% { opacity: 0; } }
            `}</style>
        </div>
    );
}

// ── Step 2 — mini 2-row × 4-col cascade grid mirroring Round 3 ────
function StepTwoVisual() {
    const rows = [
        { label: "Technology", cells: [
            { tint: "pos", short: "adoption firms" },
            { tint: "pos", short: "scale ramps"    },
            { tint: "pos", short: "diffuses"       },
            { tint: "pos", short: "reshapes"       },
        ]},
        { label: "Climate",    cells: [
            { tint: "neu", short: "steady"         },
            { tint: "pos", short: "nudge up"       },
            { tint: "pos", short: "compounds"      },
            { tint: "pos", short: "saturates"      },
        ]},
    ];
    return (
        <div className="w-full rounded-lg border border-border bg-panel/60 shadow-sm p-3">
            <div className="grid grid-cols-[68px_repeat(4,minmax(0,1fr))] gap-1.5 items-center">
                <div />
                {["I", "N", "S", "G"].map((h, i) => (
                    <div key={h} className="text-[8px] font-mono uppercase tracking-widest text-ink-3 text-center">
                        {["Imm.", "Near.", "Strat.", "Gen."][i]}
                    </div>
                ))}
                {rows.map((row, r) => (
                    // Issue #69 — the fragment shorthand (<>...</>) can't take a
                    // `key` prop, but it's the element .map() returns directly,
                    // so React logged a missing-key warning on every render
                    // regardless of the inner divs' own keys. Named `Fragment`
                    // keeps the exact same DOM output (label + cells still land
                    // as direct children of the grid, one column each) while
                    // satisfying the key requirement — a wrapping <div> here
                    // would break the grid's column alignment with the "I N S
                    // G" header row above.
                    <Fragment key={row.label}>
                        <div className="text-[10px] font-medium text-ink-2 leading-tight capitalize">
                            {row.label}
                        </div>
                        {row.cells.map((c, cIdx) => (
                            <div
                                key={`c-${r}-${cIdx}`}
                                className="rounded-sm border p-1"
                                style={
                                    c.tint === "pos"
                                        ? { background: "rgba(34,100,68,0.06)", borderColor: "rgba(34,100,68,0.20)" }
                                        : { background: "rgba(15,23,42,0.03)", borderColor: "rgba(15,23,42,0.10)" }
                                }
                            >
                                <div className="text-[8px] font-mono uppercase tracking-wider text-ink-3">
                                    {["I","N","S","G"][cIdx]}{r + 1}
                                </div>
                                <div className="text-[9.5px] leading-tight text-ink-2">
                                    {c.short}
                                </div>
                            </div>
                        ))}
                    </Fragment>
                ))}
            </div>
        </div>
    );
}

// ── Step 3 — three perspective-tilted preview cards ───────────────
function StepThreeVisual() {
    const tilt = (deg) => ({
        transform: `perspective(800px) rotateY(${deg}deg) rotateX(4deg)`,
        transformOrigin: "center center",
    });
    return (
        <div className="w-full h-full flex items-end justify-center gap-3">
            <div
                className="w-28 h-40 rounded-md border border-border bg-surface shadow-md flex flex-col p-2"
                style={tilt(14)}
            >
                <div className="text-[8px] font-mono uppercase tracking-widest text-brand mb-1">
                    Brief
                </div>
                <div className="text-[10px] font-heading font-semibold leading-tight text-ink-1 mb-1.5">
                    Corporate cloud upside compounds…
                </div>
                <div className="space-y-0.5">
                    <div className="h-1 bg-ink-3/25 rounded-sm w-full" />
                    <div className="h-1 bg-ink-3/25 rounded-sm w-11/12" />
                    <div className="h-1 bg-ink-3/25 rounded-sm w-9/12" />
                </div>
            </div>
            <div
                className="w-36 h-44 rounded-md border border-border bg-ink-1 shadow-lg flex flex-col p-2 text-white"
                style={tilt(0)}
            >
                <div className="text-[8px] font-mono uppercase tracking-widest text-white/60 mb-1">
                    Slide I
                </div>
                <div className="text-[11px] font-heading font-semibold leading-tight mb-1.5">
                    Present the story
                </div>
                <div className="mt-auto space-y-0.5">
                    <div className="h-1 bg-white/40 rounded-sm w-11/12" />
                    <div className="h-1 bg-white/25 rounded-sm w-9/12" />
                </div>
            </div>
            <div
                className="w-28 h-40 rounded-md border border-border bg-surface shadow-md flex flex-col p-2"
                style={tilt(-14)}
            >
                <div className="text-[8px] font-mono uppercase tracking-widest text-brand mb-1">
                    Next 3
                </div>
                <div className="space-y-1.5 mt-1">
                    {["Communicate", "Pre-position", "Publish check"].map((t) => (
                        <div key={t} className="text-[9.5px] leading-tight text-ink-1 font-medium">
                            · {t}…
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────────
 * PROCESS STEPS — Describe → Detect → Simulate → Audit
 * ────────────────────────────────────────────────────────────────── */
const PROCESS_STEPS = [
    {
        n: 1,
        title: "Describe",
        blurb:
            "Type the decision in plain language. The intake step extracts the actor, scale, domains affected, and reversibility.",
    },
    {
        n: 2,
        title: "Detect",
        blurb:
            "Dynamic archetype detection selects the calibrated prior bundle. Out-of-scope decisions short-circuit here.",
    },
    {
        n: 3,
        title: "Analyse",
        blurb:
            "Hundreds of stratified futures per domain, coupled by decision-specific weights. Bit-identical on same seed.",
    },
    {
        n: 4,
        title: "Audit",
        blurb:
            "Every stage's I/O cryptographically sealed. Anyone can replay a run from its audit record alone.",
    },
];

function ProcessStepsSection() {
    return (
        <section className="bg-panel py-28">
            <div className="max-w-wide mx-auto px-6 sm:px-10">
                <div className="text-center mb-16 reveal">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-ink-2 mb-3">
                        The pipeline
                    </div>
                    <h2 className="font-heading text-4xl sm:text-[42px] font-semibold text-ink-1 tracking-tight leading-tight max-w-[720px] mx-auto">
                        Four stages, every run.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {PROCESS_STEPS.map((s) => (
                        <div
                            key={s.n}
                            className="relative bg-surface rounded-lg p-7 pt-10 shadow-sm border border-transparent
                                       hover:shadow-lg hover:-translate-y-1 hover:border-border
                                       transition-all duration-250 ease-out overflow-hidden reveal"
                        >
                            <span className="process-numeral" aria-hidden="true">
                                {s.n.toString().padStart(2, "0")}
                            </span>
                            <div className="relative z-10">
                                <div className="text-[11px] uppercase tracking-widest font-semibold text-brand mb-3">
                                    Step {s.n}
                                </div>
                                <div className="font-heading text-xl font-semibold text-ink-1 tracking-tight mb-3">
                                    {s.title}
                                </div>
                                <p className="text-[14px] text-ink-2 leading-relaxed">
                                    {s.blurb}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────────────────────────────
 * ACCURACY STATS — dark section, GSAP counter animation.
 * ────────────────────────────────────────────────────────────────── */
function AccuracyStatsSection() {
    const stats = useLiveStats();
    const backtest = useBacktestSummary();
    const reduced = useReducedMotion();
    const rootRef = useRef(null);

    // Phase 19.4 — the direction-accuracy number is *always* the live
    // backtest value. If the backtest hasn't run yet (early data) we
    // fall back to null so the card renders a placeholder rather than a
    // fabricated number.
    const directionAccuracy = backtest.direction != null
        ? Number((backtest.direction * 100).toFixed(1))
        : null;
    const validatedCases    = backtest.scored ?? (stats?.analyses_sealed ?? 0);

    const metrics = [
        {
            value: directionAccuracy ?? 0,
            decimals: 1,
            suffix: "%",
            label: "Direction Accuracy",
            isLive: directionAccuracy != null,
            fallbackLabel: directionAccuracy == null ? "early data" : null,
        },
        { value: 2.3,               decimals: 1, suffix: "s",  label: "Analysis Time",        isLive: false, fallbackLabel: null },
        { value: validatedCases,    decimals: 0, suffix: "",   label: "Validated Cases",     isLive: !!backtest.data, fallbackLabel: null },
        { value: 12,                decimals: 0, suffix: "+",  label: "Domain Types",         isLive: false, fallbackLabel: null },
    ];

    useEffect(() => {
        if (reduced || !rootRef.current) return;
        const ctx = gsap.context(() => {
            gsap.utils.toArray(".stat-value").forEach((el) => {
                if (el.dataset.fallback) return;    // "early data" card — skip counter
                const end = parseFloat(el.dataset.end);
                const decimals = parseInt(el.dataset.decimals || "0", 10);
                const suffix = el.dataset.suffix || "";
                const val = { n: 0 };
                gsap.to(val, {
                    n: end,
                    duration: 1.8,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: el,
                        start: "top 80%",
                        once: true,
                    },
                    onUpdate: () => {
                        el.textContent = val.n.toFixed(decimals) + suffix;
                    },
                });
            });
        }, rootRef);
        return () => ctx.revert();
    }, [reduced, stats, backtest.data]);

    return (
        <section ref={rootRef} className="bg-deep py-28 text-white">
            <div className="max-w-wide mx-auto px-6 sm:px-10">
                <div className="text-center mb-16 reveal">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-white/50 mb-3">
                        Track record
                    </div>
                    <h2 className="font-heading text-4xl sm:text-[42px] font-semibold text-white tracking-tight leading-tight max-w-[720px] mx-auto">
                        Accuracy that's audited, not asserted.
                    </h2>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {metrics.map((m, i) => (
                        <div
                            key={i}
                            className="bg-white/5 border border-white/10 rounded-lg p-7 backdrop-blur-glass reveal"
                            data-testid={`stat-card-${i}`}
                        >
                            <div
                                className="stat-value font-heading text-[52px] font-bold text-white leading-none tracking-tight tabular-nums"
                                data-end={m.value}
                                data-decimals={m.decimals}
                                data-suffix={m.suffix}
                                data-fallback={m.fallbackLabel || undefined}
                            >
                                {/* Initial render — will be overwritten by GSAP counter.
                                    §9.5.1 hotfix — when we don't have live data yet
                                    (fallbackLabel is set), render an em-dash at the
                                    52px stat-value slot instead of dumping the
                                    calibration-state phrase in there. The phrase
                                    itself surfaces as a small pill next to the label
                                    below (see the `Early data` chip after the label). */}
                                {m.fallbackLabel
                                    ? "—"
                                    : reduced ? `${m.value.toFixed(m.decimals)}${m.suffix}` : `0${m.suffix}`}
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                                <div className="text-[13px] font-medium text-white/80">{m.label}</div>
                                {m.isLive && (
                                    <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm bg-brand-light/30 text-white/70">
                                        Live
                                    </span>
                                )}
                                {m.fallbackLabel && (
                                    <span
                                        className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm bg-white/10 text-white/70"
                                        data-testid={`stat-card-${i}-fallback-pill`}
                                    >
                                        {m.fallbackLabel}
                                    </span>
                                )}
                            </div>
                            {i === 0 && (
                                <div className="mt-4 pt-3 border-t border-white/10" data-testid="landing-accuracy-sparkline-wrap">
                                    <div className="text-[9px] uppercase tracking-widest font-semibold text-white/70 mb-2">
                                        30-day trend
                                    </div>
                                    <BacktestSparkline
                                        variant="dark"
                                        width={220}
                                        height={36}
                                        testId="landing-accuracy-sparkline"
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────────────────────────────
 * CASCADE NETWORK — Phase I marketing preview.
 *
 * 19.2 shipped the interactive CascadeNetwork component for the
 * analysis surface. The landing page can't render that (no
 * simulation data yet on marketing pages) so we now show a static
 * decorative preview via `CascadePreview` — a hand-composed SVG
 * with the Science Lab aesthetic (indigo core, concentric horizon
 * rings, 8 domain nodes, curved edges, subtle animated particles).
 * The old "coming next · Phase 19.2" placeholder was retired here.
 * ────────────────────────────────────────────────────────────────── */
function CascadeNetworkSection() {
    const reduced = useReducedMotion();
    const rootRef = useRef(null);
    useEffect(() => {
        if (reduced || !rootRef.current) return;
        const ctx = gsap.context(() => {
            // Zoom-in reveal for the CascadePreview graphic — scrubs
            // from 0.9 scale → 1.0 scale as the section scrolls into
            // view. Small enough that it reads as depth, not as an
            // interactive animation.
            gsap.fromTo(
                ".cascade-preview-parallax",
                { scale: 0.92, y: 40 },
                {
                    scale: 1, y: 0,
                    ease: "none",
                    scrollTrigger: {
                        trigger: rootRef.current,
                        start: "top 85%",
                        end: "top 25%",
                        scrub: true,
                    },
                },
            );
        }, rootRef);
        return () => ctx.revert();
    }, [reduced]);
    return (
        <section ref={rootRef} className="bg-surface py-28 reveal relative overflow-hidden" data-testid="section-cascade-preview">
            <div className="max-w-wide mx-auto px-6 sm:px-10">
                <div className="text-center mb-14 reveal">
                    <div className="eyebrow mb-3">Cascade preview</div>
                    <h2 className="font-heading text-4xl sm:text-[42px] font-semibold text-ink-1 tracking-tight leading-tight max-w-[720px] mx-auto">
                        Every consequence has a network.
                    </h2>
                    <p className="text-[16px] text-ink-2 leading-relaxed max-w-[600px] mx-auto mt-4">
                        Each analysis renders as an interactive network. Nodes are life areas,
                        edges are how one moves the next, and the rings mark short-, medium-, and long-term horizons.
                    </p>
                </div>
                <div className="reveal cascade-preview-parallax">
                    <CascadePreview />
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────────────────────────────
 * DOMAIN BREADTH — marquee tag cloud. Communicates "any domain that
 * matters" without implying a false ceiling (v5 dynamic detection).
 * ────────────────────────────────────────────────────────────────── */
const DOMAIN_TAGS_ROW_A = [
    "Monetary Policy", "Trade Flows", "Healthcare Access", "Employment",
    "Regulatory Risk", "Environmental Impact", "Alliance Dynamics",
    "Consumer Prices", "Innovation Rate", "Political Capital", "Public Health",
    "Financial Stability", "Currency Markets", "Energy Prices", "Legal Compliance",
    "Social Equity", "Data Privacy", "Supply Chain", "Immigration Flows",
    "Housing Markets",
];
const DOMAIN_TAGS_ROW_B = [
    "Labour Rights", "Cybersecurity Posture", "Constitutional Norms",
    "Industrial Capacity", "Rural Economies", "Digital Infrastructure",
    "Educational Access", "Refugee Flows", "Diplomatic Standing",
    "Market Concentration", "Corporate Governance", "Voter Turnout",
    "Public Debt", "Wage Growth", "Innovation Pipeline", "Manufacturing Base",
    "Research Funding", "Foreign Investment", "Regional Development",
    "Media Freedom",
];

function DomainCategorySection() {
    return (
        <section className="bg-surface py-28 overflow-hidden" data-testid="landing-domain-breadth">
            <div className="max-w-wide mx-auto px-6 sm:px-10">
                <div className="text-center mb-14 reveal">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-ink-2 mb-3">
                        Coverage
                    </div>
                    <h2 className="font-heading text-4xl sm:text-[42px] font-semibold text-ink-1 tracking-tight leading-tight max-w-[820px] mx-auto">
                        Any institutional decision. Any domain that matters.
                    </h2>
                    <p className="text-[16px] text-ink-2 leading-relaxed max-w-[640px] mx-auto mt-4">
                        Tomauro detects the domains relevant to your specific decision —
                        from monetary policy to healthcare access to defence posture —
                        and maps the consequence across each one.
                    </p>
                </div>

                {/* Two-row marquee. Duplicated content per row for seamless wrap. */}
                <div className="reveal">
                    <div className="domain-marquee mb-3">
                        <div className="domain-marquee__track">
                            {[...DOMAIN_TAGS_ROW_A, ...DOMAIN_TAGS_ROW_A].map((tag, i) => (
                                <span key={`a-${i}`} className="domain-tag">{tag}</span>
                            ))}
                        </div>
                    </div>
                    <div className="domain-marquee">
                        <div className="domain-marquee__track domain-marquee__track--reverse">
                            {[...DOMAIN_TAGS_ROW_B, ...DOMAIN_TAGS_ROW_B].map((tag, i) => (
                                <span key={`b-${i}`} className="domain-tag">{tag}</span>
                            ))}
                        </div>
                    </div>
                </div>

                <p className="text-center text-[13px] text-ink-2 mt-8 max-w-[520px] mx-auto reveal">
                    Domain detection adapts to every decision — the list above is a sample, not a ceiling.
                </p>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────────────────────────────
 * FEATURE CARDS — 3 × 2 grid of platform capabilities.
 * ────────────────────────────────────────────────────────────────── */
// 2026-07-27 Round 4 · CHANGE 35 — Platform capabilities refresh.
//
// Retired copy that referenced deleted surfaces (Methodology / Calibration
// pages) and forecast/accuracy framing. Replaced with the current canonical
// capability list: consequence mapping, strategic lens, nine-section brief,
// grounded AI guide, verifiable audit chain, and qualitative-outputs stance
// (no false precision). Icons preserved so the visual rhythm of the
// section is unchanged.
const FEATURES = [
    {
        icon: Network,
        title: "Multi-domain consequence mapping",
        blurb: "See how a decision propagates across interconnected domains and time horizons — from immediate impact to generational effect.",
    },
    {
        icon: GitBranch,
        title: "Strategic lens",
        blurb: "Reframe any decision through canonical lenses that surface tradeoffs you cannot see from inside the frame.",
    },
    {
        icon: LayoutGrid,
        title: "Nine-section decision brief",
        blurb: "Every analysis produces a decision-ready brief: headline, narrative, risks, opportunities, next steps, blindspot, and sensitivity spotlight.",
    },
    {
        icon: Activity,
        title: "Grounded AI guide",
        blurb: "Ask questions about your specific analysis. The guide answers from the analysis's own outputs and refuses ungrounded speculation.",
    },
    {
        icon: Fingerprint,
        title: "Verifiable audit chain",
        blurb: "Every analysis is cryptographically signed. Anyone with the audit link can verify the report came from Tomauro, unaltered.",
    },
    {
        icon: TrendingUp,
        title: "Qualitative outputs, no false precision",
        blurb: "Every read is a qualitative interpretation of a structural analysis — no outcome bands dressed up as certainties, no accuracy theatre.",
    },
];

function FeatureCardsSection() {
    return (
        <section className="bg-panel py-28">
            <div className="max-w-wide mx-auto px-6 sm:px-10">
                <div className="text-center mb-14 reveal">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-ink-2 mb-3">
                        Platform
                    </div>
                    <h2 className="font-heading text-4xl sm:text-[42px] font-semibold text-ink-1 tracking-tight leading-tight max-w-[720px] mx-auto">
                        Everything a serious diligence process needs.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {FEATURES.map(({ icon: Icon, title, blurb }) => (
                        <div
                            key={title}
                            className="bg-surface rounded-lg p-7 shadow-sm border border-transparent
                                       hover:shadow-lg hover:-translate-y-1 hover:border-border
                                       transition-all duration-250 ease-out reveal"
                        >
                            <div className="w-11 h-11 rounded-md bg-brand-light flex items-center justify-center mb-5">
                                <Icon size={22} className="text-brand" />
                            </div>
                            <div className="font-heading text-lg font-semibold text-ink-1 tracking-tight mb-2">
                                {title}
                            </div>
                            <p className="text-[14px] text-ink-2 leading-relaxed">
                                {blurb}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────────────────────────────
 * TESTIMONIALS — labelled placeholders.
 * ────────────────────────────────────────────────────────────────── */
const TESTIMONIALS = [
    {
        quote: "The audit chain closed the last diligence gap. We can prove why we made the call.",
        who: "Head of Risk",
        where: "Systemically-important bank",
    },
    {
        quote: "Three seconds from a sentence to a defensible consequence fan. That's the whole game.",
        who: "Chief Strategy Officer",
        where: "Fortune 100 industrial",
    },
    {
        quote: "The divergent-path detection surfaced a scenario we'd missed for six months.",
        who: "Managing Director",
        where: "Sovereign pension fund",
    },
];

function TestimonialSection() {
    return (
        <section className="bg-surface py-28">
            <div className="max-w-wide mx-auto px-6 sm:px-10">
                <div className="text-center mb-14 reveal">
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-ink-2 mb-3">
                        Signal — placeholder testimonials
                    </div>
                    <h2 className="font-heading text-4xl sm:text-[42px] font-semibold text-ink-1 tracking-tight leading-tight max-w-[720px] mx-auto">
                        Built for people who own the decision.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {TESTIMONIALS.map((t, i) => (
                        <div
                            key={i}
                            className="bg-panel rounded-lg p-7 border border-border reveal"
                        >
                            <div className="text-brand text-4xl font-heading leading-none mb-3">“</div>
                            <p className="text-[16px] text-ink-1 leading-relaxed mb-6">
                                {t.quote}
                            </p>
                            <div className="text-[13px] font-semibold text-ink-1">{t.who}</div>
                            <div className="text-[12px] text-ink-2">{t.where}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────────────────────────────────
 * PRE-FOOTER CTA — dark navy band. Rewritten in Phase-W blank-space
 * pass to fill the previously-empty pre-footer space with real content.
 * Now carries a live proof line pulled from useBacktestSummary() so
 * every visitor sees the current backtest number.
 * ────────────────────────────────────────────────────────────────── */
function CTASection() {
    const reduced = useReducedMotion();
    const rootRef = useRef(null);
    const { fire, fireOnce } = useLandingTelemetry();
    // Phase Y — fire the impression once per session when the CTA
    // band enters the viewport (30% threshold matches Signal).
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const io = new IntersectionObserver((entries) => {
            for (const e of entries) {
                if (e.isIntersecting) {
                    fireOnce("landing_prefooter_cta_impression");
                    io.disconnect();
                    break;
                }
            }
        }, { threshold: 0.3 });
        io.observe(el);
        return () => io.disconnect();
    }, [fireOnce]);

    return (
        <section
            ref={rootRef}
            className="relative py-20 px-6 text-center overflow-hidden"
            style={{ background: "#0F1629" }}
            data-testid="section-prefooter-cta"
        >
            {/* Ambient background — radial glow behind the headline */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(circle at 50% 40%, rgba(26,35,126,0.18) 0%, rgba(15,22,41,0) 55%)",
                }}
            />
            {/* Ambient floating dots — drift slowly, respect reduced-motion. */}
            {!reduced && (
                <>
                    <CTADot top="20%" left="14%" delay={0}   duration={11} />
                    <CTADot top="60%" left="82%" delay={2.4} duration={13} />
                    <CTADot top="80%" left="30%" delay={4.2} duration={9}  />
                </>
            )}

            <div className="relative max-w-[720px] mx-auto">
                <div
                    className="text-[11px] uppercase font-semibold mb-5 reveal"
                    style={{ color: "#94A3B8", letterSpacing: "0.1em" }}
                    data-testid="prefooter-cta-caption"
                >
                    Ready to see it work
                </div>
                <h2
                    className="font-heading font-semibold tracking-tight text-white reveal"
                    style={{ fontSize: "clamp(1.75rem, 3.5vw, 2rem)", lineHeight: 1.2 }}
                    data-testid="prefooter-cta-headline"
                >
                    Test-run a real decision. See the whole cascade end to end.
                </h2>
                <p
                    className="mt-5 mx-auto text-[16px] reveal"
                    style={{ color: "#94A3B8", lineHeight: 1.6, maxWidth: 620 }}
                    data-testid="prefooter-cta-sub"
                >
                    No credit card. No signup wall. The engine runs live against the same
                    deterministic core your production analyses use.
                </p>

                <div
                    className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10 reveal"
                    data-testid="prefooter-cta-actions"
                >
                    <Link
                        to="/analyses/new"
                        data-testid="prefooter-cta-primary"
                        onClick={() => fire("landing_prefooter_cta_primary_click")}
                        className="group inline-flex items-center gap-2 rounded-md font-semibold text-[15px]
                                   text-white shadow-lg transition-all duration-200 ease-out
                                   hover:-translate-y-0.5 hover:shadow-xl"
                        style={{ background: "#1A237E", padding: "14px 28px" }}
                    >
                        Start a free analysis
                        <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

function CTADot({ top, left, delay, duration }) {
    return (
        <span
            className="prefooter-cta-dot pointer-events-none"
            style={{
                position: "absolute",
                top, left,
                width: 6, height: 6,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.06)",
                boxShadow: "0 0 12px rgba(255, 255, 255, 0.08)",
                animation: `cta-dot-drift ${duration}s ease-in-out ${delay}s infinite`,
            }}
            aria-hidden
        />
    );
}

/* ─────────────────────────────────────────────────────────────────────
 * PAGE ROOT
 * ────────────────────────────────────────────────────────────────── */
export default function Landing() {
    // On mount, refresh ScrollTrigger so any elements laid out after
    // fonts finished loading get their triggers recomputed. (Font swap
    // can shift the y-position of `.reveal` targets.)
    useEffect(() => {
        const t = setTimeout(() => ScrollTrigger.refresh(), 250);
        return () => clearTimeout(t);
    }, []);

    return (
        <div className="landing-root bg-page" data-testid="landing-page">
            <Nav />
            {/* Phase Y — fire landing_view on mount, and expose section-impression observers below. */}
            <LandingTelemetryBootstrap />
            {/* Phase v5.3 §9.4 — LandingMorphs removed with THE SIGNAL section
                (2026-07-07). Both scroll-linked morphs used data-testids that
                lived only inside TheSignalSection ("section-the-signal",
                "signal-widget-accuracy-value", "signal-mini-fan"), so the
                morph hook had no live triggers left to attach to. */}
            <HeroSection />
            {/* Phase Z — impact-clock card; renders null for anonymous / empty-queue */}
            <LandingImpactCard />
            {/* 2026-07-27 CHANGE 1 (revised per user HOLD) — Home-page section
                removals from the CHANGES 1-8 batch. Dropped:
                ThreeLayerArchitectureSection, SeeItInActionSection,
                AccuracyStatsSection, CascadeNetworkSection, ProcessStepsSection.
                RETAINED (HOLD): HowItWorksSection — user reversed the removal
                pending a redesigned version (delivered in Round 4 CHANGE 21).
                Also retained: LandingImpactCard, DomainCategorySection,
                FeatureCardsSection, TestimonialSection, CTASection. */}
            <DomainCategorySection />
            <FeatureCardsSection />
            <TestimonialSection />
            {/* 2026-07-27 CHANGE 25 — removed the duplicate
                <FeatureCardsSection /> + <TestimonialSection /> render pair
                that had crept in below the canonical trio. Root cause was
                a stray copy left over from the Round 1 Change 1 cleanup;
                each of these sections is a singleton and rendering them
                twice produced visible duplicates ("Platform", "Signal —
                placeholder testimonials") on every home-page load. */}
            {/* 2026-07-27 Round 4 · CHANGE 21 — How-it-works redesigned and
                moved from just below the hero-card to be the LAST content
                section on the page, immediately above the dark CTA+Footer
                closer wrapper. */}
            <HowItWorksSection />
            {/* Wrap pre-footer CTA + Footer in a shared dark surface so the
                Footer's `mt-32` (128 px top margin) renders on the same
                dark background — no visible white gap between them. */}
            <div style={{ background: "#0F1629" }}>
                <CTASection />
                <Footer showPatentPending />
            </div>
        </div>
    );
}

/* Phase Y — bootstrap the telemetry hook once (fires landing_view on mount).
 * Kept as a separate component so we don't have to convert the Landing
 * default export into an inner component. */
function LandingTelemetryBootstrap() {
    useLandingTelemetry();
    return null;
}

/* Phase v5.3 §9.4 — LandingMorphs was here (deleted 2026-07-07 with the
 * "THE SIGNAL" section). Both morphs targeted data-testids that only
 * existed inside TheSignalSection ("section-the-signal",
 * "signal-widget-accuracy-value", "signal-mini-fan"). If a future
 * shared-element morph lands, wire it against live testids rather
 * than resurrect this shell. */
